import React, { Suspense } from 'react';

import { Route, Routes } from 'react-router-dom';

import Loading from './components/loading';
import MainLayout from './Layouts/MainLayout';
import WorkshopLayout from './Layouts/WorkshopLayout';
import Login from './pages/Login';
import VMSLogin from './pages/ECMLogin';
import NotFound from './pages/NotFound';
import ServiceCalendar from './pages/ServiceCalendar';
import routeConfig from './RouteConfig';
import { PermissionGuard, Unauthorized } from './utils/PermissionGuard';

const RouteHandler = ({ Component, pageName, iframeUrl, ...restProps }) => {
    if (iframeUrl && (iframeUrl.startsWith('http://') || iframeUrl.startsWith('https://'))) {
        return (
            <div className="iframeview" style={{ width: '100%', height: '100vh' }}>
                <iframe src={iframeUrl} title={pageName} style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
        );
    }
    return (
        <Suspense fallback={<Loading />}>
            <Component pageName={pageName} {...restProps} />
        </Suspense>
    );
};

const AuthorizedRouteHandler = ({ Component, pageName, iframeUrl, requiredPermission = null, ...restProps }) => {
    if (!requiredPermission) {
        console.warn(`Route for ${pageName} is missing permission config.`);
        return <RouteHandler Component={Component} pageName={pageName} iframeUrl={iframeUrl} {...restProps} />;
    }
    return (
        <PermissionGuard requiredPermission={requiredPermission} fallback={<Unauthorized />}>
            <RouteHandler Component={Component} pageName={pageName} iframeUrl={iframeUrl} {...restProps} />
        </PermissionGuard>
    );
};

const AppRoutes = () => {
    const renderRoute = (route) => {
        const Component = route.element;
        const elementProps = {
            Component: Component,
            pageName: route.pageName || 'Page',
            iframeUrl: route.iframeUrl // Pass iframeUrl if defined in config
        };

        let routeElement;
        if (route.auth) {
            routeElement = <AuthorizedRouteHandler {...elementProps} requiredPermission={route?.auth?.permission} />;
        } else {
            routeElement = <RouteHandler {...elementProps} />;
        }

        return <Route key={route.path} path={route.path} element={routeElement} />;
    };

    const mainLayoutRoutes = routeConfig.filter((r) => r.layout === 'main');
    const workshopLayoutRoutes = routeConfig.filter((r) => r.layout === 'workshop');
    const standaloneRoutes = routeConfig.filter((r) => r.layout === 'none');

    return (
        <Routes>
            {/* Standalone routes (Workshop Login, Logout, etc.) */}
            {standaloneRoutes.map(renderRoute)}
            <Route index element={<Login />} />
            <Route path="/vms-login" element={<VMSLogin />} />

            {/* Routes within MainLayout */}
            <Route path="/" element={<MainLayout />}>
                <Route index element={<ServiceCalendar />} />

                {mainLayoutRoutes.filter((r) => r.path !== '*').map(renderRoute)}
            </Route>

            {/* Routes within WorkshopLayout */}
            <Route path="/service" element={<WorkshopLayout />}>
                {workshopLayoutRoutes.filter((r) => r.path !== '*').map(renderRoute)}
            </Route>

            <Route
                path="*"
                element={
                    <Suspense fallback={<Loading />}>
                        <NotFound />
                    </Suspense>
                }
            />
        </Routes>
    );
};

export default AppRoutes;
